import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/solid"
import React, { useEffect, useState } from "react"
import { useMutation, useQuery } from "urql"

import PaymentProviderWrapper from "../../components/payments/PaymentProviderWrapper"
import { Button } from "../../components/shared/Buttons"
import CurrencyInput from "../../components/shared/CurrencyInput"
import { Label } from "../../components/shared/Inputs"
import { Banner, Flyout } from "../../components/shared/Layout"
import LoadingSpinner from "../../components/shared/LoadingSpinner"
import { AnimatedModal } from "../../components/shared/Modal"
import { usePractice } from "../../contexts/PracticeContext"
import { formatPrice, unformatPrice } from "../../utils/utils"

import BookableEventPaymentModal from "./BookableEventPaymentModal"
import { canEditRegistrationPrice, getPaymentStatusDisplay } from "./bookableEventPaymentStatus"
import {
  REMOVE_CLIENT_FROM_BOOKABLE_EVENT_MUTATION,
  UPDATE_BOOKABLE_EVENT_REGISTRATION_AMOUNT_MUTATION
} from "./mutations"
import { BOOKABLE_EVENT_REGISTRATION_QUERY } from "./queries"
import RegistrationPaymentActions from "./RegistrationPaymentActions"
import RequestEventRegistrationPaymentModal from "./RequestEventRegistrationPaymentModal"

export default function ClientDetailsFlyout({ visible, closeFlyout, client, bookableEventId, onClientRemoved }) {
  const [removeError, setRemoveError] = useState(null)
  const [priceError, setPriceError] = useState(null)
  const [editAmountCents, setEditAmountCents] = useState(null)
  const [{ fetching: removeFetching }, removeClientFromEvent] = useMutation(REMOVE_CLIENT_FROM_BOOKABLE_EVENT_MUTATION)
  const [{ fetching: updatePriceFetching }, updateRegistrationAmount] = useMutation(
    UPDATE_BOOKABLE_EVENT_REGISTRATION_AMOUNT_MUTATION
  )
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [showRequestPaymentModal, setShowRequestPaymentModal] = useState(false)
  const { practice } = usePractice()

  const [{ data: registrationData, fetching: registrationFetching, error: registrationError }, reexecuteQuery] =
    useQuery({
      query: BOOKABLE_EVENT_REGISTRATION_QUERY,
      variables: {
        bookableEventId,
        clientId: client?.id
      },
      pause: !visible || !client?.id
    })

  const registration = registrationData?.bookableEventRegistration

  useEffect(() => {
    if (registration?.amountCents != null) {
      setEditAmountCents(registration.amountCents)
    }
  }, [registration?.amountCents])

  const formatCurrency = (amountCents) => `$${(amountCents / 100).toFixed(2)}`

  const canEditPrice = () => canEditRegistrationPrice(registration)

  const handleSavePrice = async () => {
    if (!client || editAmountCents == null) return

    setPriceError(null)
    try {
      const result = await updateRegistrationAmount({
        bookableEventId,
        clientId: client.id,
        amountCents: editAmountCents
      })

      if (result.data?.updateBookableEventRegistrationAmount?.result === "success") {
        reexecuteQuery({ requestPolicy: "network-only" })
      } else {
        const errorMessage = result.data?.updateBookableEventRegistrationAmount?.errors?.[0] || "Failed to update price"
        setPriceError(errorMessage)
      }
    } catch (err) {
      setPriceError(err.message || "Failed to update price")
    }
  }

  const paymentStatus = registration
    ? getPaymentStatusDisplay(registration.paymentStatus, { bookedBy: registration.bookedBy })
    : { label: "Unknown", color: "text-gray-dark" }

  const handleShowRemoveConfirmation = () => {
    setShowRemoveConfirmation(true)
  }

  const handleCancelRemove = () => {
    setShowRemoveConfirmation(false)
    setRemoveError(null)
  }

  const handleRemoveClient = async () => {
    if (!client) return

    setRemoveError(null)

    try {
      const result = await removeClientFromEvent({
        bookableEventId: bookableEventId,
        clientId: client.id
      })

      if (result.data?.removeClientFromBookableEvent?.result === "success") {
        setShowRemoveConfirmation(false)
        onClientRemoved()
        closeFlyout()
      } else {
        const errorMessage =
          result.data?.removeClientFromBookableEvent?.errors?.[0] || "Failed to remove client from event"
        setRemoveError(errorMessage)
      }
    } catch (err) {
      console.error("Error removing client:", err) // eslint-disable-line no-console
      setRemoveError(err.message || "An unexpected error occurred")
    }
  }

  const handleShowPaymentModal = () => {
    setShowPaymentModal(true)
  }

  const handlePaymentSuccess = () => {
    reexecuteQuery({ requestPolicy: "network-only" })
  }

  return (
    <>
      <Flyout header="Client Details" visible={visible} closeFlyout={closeFlyout}>
        <div className="space-y-6">
          <div>
            <h3 className="mb-2 font-semibold">Client Information</h3>
            <div className="rounded-lg border border-gray-light p-4">
              <div className="mb-2 flex items-center">
                <span className="font-medium">Name:</span>
                <span className="mx-1">
                  {client?.firstName} {client?.lastName}
                </span>
                <a
                  href={`/portal/clients/${client?.id}`}
                  className="ml-1 inline-flex items-center text-teal hover:text-teal-dark"
                  title="View client profile"
                  target="_blank"
                  rel="noopener noreferrer">
                  <ArrowTopRightOnSquareIcon className="size-5" />
                </a>
              </div>
              <div className="mb-2">
                <span className="font-medium">Email:</span> {client?.email || "N/A"}
              </div>
              <div>
                <span className="font-medium">Phone:</span> {client?.phone || "N/A"}
              </div>
            </div>
          </div>

          <div>
            <h3 className="mb-2 font-semibold">Payment Status</h3>
            {registrationFetching ? (
              <div className="flex justify-center py-4">
                <LoadingSpinner />
              </div>
            ) : registrationError ? (
              <div className="rounded-lg border border-red p-4 text-red">Error loading payment information</div>
            ) : (
              <div className="rounded-lg border border-gray-light p-4">
                {!registration ? (
                  <div className="text-center italic text-gray-dark">Registration information not found</div>
                ) : (
                  <>
                    <div className="mb-2">
                      <span className="font-medium">Status:</span>
                      <span className={`ml-2 inline-block rounded-full px-2 py-0.5 ${paymentStatus.color}`}>
                        {paymentStatus.label}
                      </span>
                    </div>
                    <div className="mb-2">
                      <span className="font-medium">Registration Date:</span>{" "}
                      {registration.createdAt ? new Date(registration.createdAt).toLocaleDateString() : "N/A"}
                    </div>
                    {registration.amountCents > 0 && !canEditPrice() && (
                      <div className="mb-2">
                        <span className="font-medium">Amount:</span> {formatCurrency(registration.amountCents)}
                      </div>
                    )}
                    {canEditPrice() && (
                      <div className="mb-4">
                        {registration.paymentStatus === "free" && (
                          <Banner type="info" className="mb-3">
                            This registration is Free ($0). Set the event price below, save, then Process or Request
                            payment.
                          </Banner>
                        )}
                        <Label htmlFor="registration-amount-edit">Amount for this client</Label>
                        <CurrencyInput
                          id="registration-amount-edit"
                          placeholder="Price"
                          value={
                            formatPrice(editAmountCents, practice?.defaultCurrency, { alwaysShowDecimals: true }) || ""
                          }
                          onChange={(e) => setEditAmountCents(unformatPrice(e.target.value))}
                        />
                        {priceError && <div className="mt-2 text-sm text-red">{priceError}</div>}
                        <Button
                          type="secondary"
                          className="mt-2 w-full"
                          onClick={handleSavePrice}
                          disabled={updatePriceFetching || editAmountCents === registration.amountCents}>
                          {updatePriceFetching ? "Saving..." : "Save price"}
                        </Button>
                      </div>
                    )}
                    {registration.discount && (
                      <>
                        <div className="mb-2">
                          <span className="font-medium">Discount Code:</span> {registration.discount.code}
                        </div>
                        <div className="mb-2">
                          <span className="font-medium">Discount Amount:</span>{" "}
                          {formatCurrency(registration.discount.amountCents)}
                        </div>
                      </>
                    )}

                    <RegistrationPaymentActions
                      registration={registration}
                      practice={practice}
                      onPaymentUpdated={() => reexecuteQuery({ requestPolicy: "network-only" })}
                      onShowPaymentModal={handleShowPaymentModal}
                      onShowRequestPaymentModal={() => setShowRequestPaymentModal(true)}
                    />
                  </>
                )}
              </div>
            )}
          </div>

          <div className="pt-4">
            <Button type="destructive" onClick={handleShowRemoveConfirmation} className="w-full">
              Remove Client from Event
            </Button>
          </div>
        </div>
      </Flyout>

      {/* Confirmation Modal */}
      <AnimatedModal
        visible={showRemoveConfirmation}
        hideModal={handleCancelRemove}
        header="Remove Client from Event"
        showFooter={true}
        cancelButtonCopy="Cancel"
        cancelButtonType="tertiary"
        actionButtonCopy={removeFetching ? "Removing..." : "Yes, Remove Client"}
        actionButtonType="destructive"
        saveDisabled={removeFetching}
        onSave={handleRemoveClient}>
        <div className="py-4">
          <p className="mb-4">
            Are you sure you want to remove{" "}
            <strong>
              {client?.firstName} {client?.lastName}
            </strong>{" "}
            from this event?
          </p>
          <p className="mb-4">This action cannot be undone.</p>
          {removeError && <div className="mt-4 rounded border border-red p-3 text-red">{removeError}</div>}
        </div>
      </AnimatedModal>

      {/* Payment Modal */}
      {client && registration && (
        <PaymentProviderWrapper practice={practice} client={client}>
          <BookableEventPaymentModal
            visible={showPaymentModal}
            hideModal={() => setShowPaymentModal(false)}
            client={client}
            registration={registration}
            onPaymentSuccess={handlePaymentSuccess}
          />
        </PaymentProviderWrapper>
      )}

      {client && registration && (
        <RequestEventRegistrationPaymentModal
          visible={showRequestPaymentModal}
          hideModal={() => setShowRequestPaymentModal(false)}
          client={client}
          registration={registration}
        />
      )}
    </>
  )
}
